/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react'
import styled from 'styled-components'
import { Competence } from '../types'
import { StrokedButton } from 'views/components/UI/Buttons'

interface CompetenceCardProps {
  competence: Competence
  onStart: (competence: Competence) => void
  onContinue: (competence: Competence) => void
  sequentialNumber: number
  allCompetences: Competence[]
}

const CardContainer = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  background: white;
  border-radius: 8px;
  padding: 24px 12px 12px 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: ${(props) => (props.isActive ? 1 : 0.5)};
  cursor: ${(props) => (props.isActive ? 'pointer' : 'not-allowed')};
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  gap: 16px;
  // height: 120px;

  &:hover {
    box-shadow: ${(props) => (props.isActive ? '0 4px 8px rgba(0, 0, 0, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.1)')};
  }
`

const CompetenceNumber = styled.div<{ isCompleted: boolean }>`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => (props.isCompleted ? '#1F2124' : '#333')};
`

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ProgressText = styled.span`
  font-size: 14px;
  color: #797a7c;
  font-weight: 500;
`

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
`

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  background: ${(props) => (props.progress === 100 ? '#c6016b' : '#c6016b')};
  width: ${(props) => props.progress}%;
  transition: width 0.3s ease-in-out;
  border-radius: 3px;
`

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 8px 24px;
  border-radius: 20px;
  border: 1px solid ${(props) => (props.variant === 'primary' ? '#2196f3' : '#e0e0e0')};
  background: ${(props) => (props.variant === 'primary' ? '#2196f3' : 'white')};
  color: ${(props) => (props.variant === 'primary' ? 'white' : '#666')};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  align-self: flex-start;

  &:hover {
    background: ${(props) => (props.variant === 'primary' ? '#1976d2' : '#f5f5f5')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StatusLabel = styled.span<{ isCompleted: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => (props.isCompleted ? '#4caf50' : '#666')};
  align-self: flex-start;
`

const CompetenceCard: React.FC<CompetenceCardProps> = ({
  competence,
  onStart,
  onContinue,
  sequentialNumber,
  allCompetences,
}) => {
  const { id, name, answeredQuestions, questions, isCompleted } = competence

  const totalQuestions = questions.length
  const progress = (answeredQuestions / totalQuestions) * 100

  // Determinar si esta tarjeta debe estar activa
  const isActive = (() => {
    // Si está completada, no debe estar activa
    if (isCompleted) return false

    // Encontrar el índice de esta competencia
    const currentIndex = allCompetences.findIndex((comp) => comp.id === id)
    if (currentIndex === -1) return false

    // La primera competencia siempre está activa si no está completada
    if (currentIndex === 0) return true

    // Para las demás, verificar que todas las anteriores estén completadas
    for (let i = 0; i < currentIndex; i++) {
      if (!allCompetences[i].isCompleted) {
        return false
      }
    }

    return true
  })()

  const getProgressText = () => {
    if (answeredQuestions === 0) return 'Iniciar'
    if (isCompleted) return 'Finalizada'
    return 'Retomar'
  }

  const handleClick = () => {
    if (!isActive) return

    if (answeredQuestions === 0) {
      onStart(competence)
    } else if (!isCompleted) {
      onContinue(competence)
    }
  }

  const handleButtonClick = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    handleClick()
  }

  const buttonText = getProgressText()

  return (
    <CardContainer
      isActive={isActive}
      isCompleted={isCompleted}
      onClick={isActive && !isCompleted ? handleClick : undefined}
    >
      <CompetenceNumber isCompleted={isCompleted}>
        {sequentialNumber}. {name}
      </CompetenceNumber>

      <ProgressContainer>
        <ProgressText>
          {answeredQuestions} de {totalQuestions} preguntas
        </ProgressText>
        <ProgressBarContainer>
          <ProgressBarFill progress={progress} />
        </ProgressBarContainer>
      </ProgressContainer>

      <StrokedButton
        style={{
          fontSize: '14px',
          fontWeight: '700',
          padding: '6px 15px',
          width: 'fit-content',
          alignSelf: 'flex-end',
          margin: 0,
        }}
        onClick={handleButtonClick}
        disabled={!isActive || isCompleted}
      >
        {buttonText}
      </StrokedButton>
    </CardContainer>
  )
}

export default CompetenceCard
